import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Palette, Upload, Save, Loader2, Image as ImageIcon, Link as LinkIcon,
  Type, Sparkles, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { applyBranding } from "../hooks/useBranding";

const FIELDS = [
  { key: "brand_app_name", label: "Nom de l'application (onglet)", icon: Type, placeholder: "Acme Corp Academy" },
  { key: "brand_login_title", label: "Titre de la page de connexion", icon: Type, placeholder: "Accelerez votre reussite" },
  { key: "brand_login_subtitle", label: "Sous-titre de la connexion", icon: Type, placeholder: "Description...", textarea: true },
  { key: "brand_slug", label: "Slug public (ex: acme)", icon: LinkIcon, placeholder: "acme-corp", mono: true, help: "Accessible sur /login?t=<slug>" },
];

const IMG_FIELDS = [
  { key: "brand_logo", label: "Logo", hint: "PNG/SVG recommande, max 3 Mo, bande horizontale 400x120px" },
  { key: "brand_login_hero_image", label: "Image de la page de connexion", hint: "Photo immersive, cote gauche du login, max 3 Mo" },
  { key: "brand_favicon", label: "Favicon (icone onglet)", hint: "32x32 .ico / .png, max 256Ko" },
];

/**
 * BrandingConfigurator
 *
 * Reusable configurator for tenant branding (colors, texts, logos, login page).
 * Used standalone on /tenant/branding, inline inside /tenant/settings, and in
 * Master Admin (with `targetTenantId` prop targeting a specific tenant).
 *
 * Props:
 *  - compact (bool): hides the standalone hero/header.
 *  - targetTenantId (string?): when provided, calls the master-admin
 *    endpoints `/admin/master/tenants/{id}/branding` and bypasses the
 *    whitelabel offer check (master admin can always edit).
 */
export function BrandingConfigurator({ compact = false, targetTenantId = null, blitzzMaster = false }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brand, setBrand] = useState(null);
  const [form, setForm] = useState({});

  const endpoint = blitzzMaster
    ? `/admin/master/blitzz/branding`
    : (targetTenantId ? `/admin/master/tenants/${targetTenantId}/branding` : `/tenant/branding`);
  const isAdminEdit = !!(targetTenantId || blitzzMaster);

  const load = useCallback(async () => {
    try {
      const r = await api.get(endpoint);
      setBrand(r.data);
      setForm(r.data || {});
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const patch = (k, v) => setForm({ ...form, [k]: v });

  const uploadFile = (key) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image trop volumineuse (max 3MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => patch(key, reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!brand?.whitelabel_enabled && !isAdminEdit) {
      toast.error("Cette offre n'inclut pas la marque blanche.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(endpoint, form);
      setBrand(r.data);
      setForm(r.data);
      // Only re-apply branding if editing the current user's tenant
      if (!isAdminEdit) applyBranding(r.data);
      toast.success("Marque blanche mise a jour");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  if (loading) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--brand-primary,#f7c935)]" /></div>;

  const wl = !!brand?.whitelabel_enabled || isAdminEdit;
  const loginUrl = form.brand_slug ? `${window.location.origin}/login?t=${form.brand_slug}` : null;

  return (
    <div data-testid="branding-configurator">
      {!compact && (
        <div className="mb-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--brand-primary,#f7c935)] font-bold mb-2">
            <Palette className="w-3.5 h-3.5" /> Marque blanche
          </div>
          <h2 className="text-2xl font-black tracking-tight text-white">Personnalisez <span style={{ color: "var(--brand-primary,#f7c935)" }}>votre marque</span></h2>
          <p className="text-slate-400 mt-1.5 text-sm max-w-2xl">Logos, couleurs, page de connexion : adaptez l'interface aux codes de votre organisme.</p>
        </div>
      )}

      {!wl && !isAdminEdit && (
        <div className="flex items-start gap-3 p-4 mb-5 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-200 text-sm" data-testid="wl-upsell-banner">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-bold mb-1">Marque blanche non incluse</div>
            <p className="text-xs text-amber-100/80">Votre offre actuelle ne comprend pas la marque blanche. Contactez votre administrateur pour activer cette option sur votre abonnement.</p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-5 gap-5">
        {/* FORM */}
        <div className="md:col-span-3 space-y-4">
          <section className="bg-[#0b0811] border border-white/10 rounded-xl p-4" data-testid="colors-section">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300 mb-3 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-[var(--brand-primary,#f7c935)]" /> Couleurs</h3>
            <div className="grid grid-cols-2 gap-3">
              {[{ k: "brand_color_primary", l: "Couleur principale" }, { k: "brand_color_secondary", l: "Couleur secondaire" }].map((c) => (
                <div key={c.k}>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5 block">{c.l}</label>
                  <div className="flex items-center gap-2">
                    <input type="color"
                      disabled={!wl}
                      value={form[c.k] || "#f7c935"}
                      onChange={(e) => patch(c.k, e.target.value)}
                      className="w-10 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer disabled:opacity-50"
                      data-testid={`color-${c.k}`}
                    />
                    <Input value={form[c.k] || ""} onChange={(e) => patch(c.k, e.target.value)}
                      placeholder="#f7c935" disabled={!wl}
                      className="flex-1 bg-[#14101c] border-white/10 text-white font-mono text-sm h-10" />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-[#0b0811] border border-white/10 rounded-xl p-4" data-testid="texts-section">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300 mb-3 flex items-center gap-2"><Type className="w-3.5 h-3.5 text-[var(--brand-primary,#f7c935)]" /> Textes et slug</h3>
            <div className="space-y-3">
              {FIELDS.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.key}>
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1 block flex items-center gap-1.5">
                      <Icon className="w-3 h-3" /> {f.label}
                    </label>
                    {f.textarea ? (
                      <textarea value={form[f.key] || ""} onChange={(e) => patch(f.key, e.target.value)}
                        placeholder={f.placeholder} disabled={!wl} rows={3}
                        className="w-full bg-[#14101c] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-[var(--brand-primary,#f7c935)] outline-none disabled:opacity-50"
                        data-testid={`field-${f.key}`}
                      />
                    ) : (
                      <Input value={form[f.key] || ""} onChange={(e) => patch(f.key, e.target.value)}
                        placeholder={f.placeholder} disabled={!wl}
                        className={"bg-[#14101c] border-white/10 text-white " + (f.mono ? "font-mono" : "")}
                        data-testid={`field-${f.key}`}
                      />
                    )}
                    {f.help && <p className="text-[10px] text-slate-500 mt-1">{f.help}</p>}
                  </div>
                );
              })}
              {loginUrl && (
                <div className="text-[10px] text-emerald-400 font-mono bg-emerald-500/10 border border-emerald-500/20 rounded px-2 py-1.5 break-all">
                  Page de connexion: {loginUrl}
                </div>
              )}
            </div>
          </section>

          <section className="bg-[#0b0811] border border-white/10 rounded-xl p-4" data-testid="images-section">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300 mb-3 flex items-center gap-2"><ImageIcon className="w-3.5 h-3.5 text-[var(--brand-primary,#f7c935)]" /> Images</h3>
            <div className="space-y-3">
              {IMG_FIELDS.map((f) => (
                <div key={f.key} className="flex items-start gap-3 bg-[#14101c] border border-white/5 rounded-lg p-3">
                  <div className="w-14 h-14 rounded-lg bg-white/5 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {form[f.key] ? <img src={form[f.key]} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white mb-0.5">{f.label}</div>
                    <p className="text-[10px] text-slate-500 mb-2">{f.hint}</p>
                    <label className={"inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-bold cursor-pointer " + (wl ? "bg-[var(--brand-primary,#f7c935)]/15 text-[var(--brand-primary,#f7c935)] hover:bg-[var(--brand-primary,#f7c935)]/25" : "bg-white/5 text-slate-500 cursor-not-allowed")}>
                      <Upload className="w-3.5 h-3.5" /> Choisir un fichier
                      <input type="file" accept="image/*" onChange={uploadFile(f.key)} className="hidden" disabled={!wl} data-testid={`upload-${f.key}`} />
                    </label>
                    {form[f.key] && (
                      <button onClick={() => patch(f.key, "")} className="ml-2 text-xs text-red-400 hover:text-red-300" data-testid={`remove-${f.key}`}>Retirer</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* PREVIEW */}
        <div className="md:col-span-2">
          <div className="sticky top-4 bg-[#0b0811] border border-white/10 rounded-xl overflow-hidden" data-testid="brand-preview">
            <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Apercu connexion</span>
              <span className="text-[9px] text-slate-600">live</span>
            </div>
            <div className="relative h-60"
              style={{
                background: form.brand_login_hero_image
                  ? `url(${form.brand_login_hero_image}) center/cover`
                  : `radial-gradient(circle at 30% 40%, ${form.brand_color_secondary || "#461d8a"} 0%, #0b0811 70%)`,
              }}>
              {form.brand_login_hero_image && <div className="absolute inset-0 bg-black/40" />}
              <div className="relative z-10 p-4">
                {form.brand_logo && <img src={form.brand_logo} alt="logo" className="h-8 object-contain mb-3" />}
                <h3 className="text-lg font-black leading-tight text-white">
                  {(form.brand_login_title || "Accelerez votre reussite").split(" ").slice(0, -1).join(" ")}{" "}
                  <span style={{ color: form.brand_color_primary || "#f7c935" }}>{(form.brand_login_title || "reussite").split(" ").slice(-1)}</span>
                </h3>
                <p className="text-[11px] text-slate-200 mt-1.5 line-clamp-3">{form.brand_login_subtitle || "Votre plateforme de pilotage."}</p>
              </div>
            </div>
            <div className="p-3 bg-[#14101c]">
              <button
                className="w-full py-2 rounded-lg font-bold text-xs"
                style={{ background: form.brand_color_primary || "#f7c935", color: "#14101c" }}
              >Se connecter</button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={!wl || saving} className="bg-[var(--brand-primary,#f7c935)] text-[#14101c] font-bold" data-testid="save-branding-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

export default BrandingConfigurator;
